/**
 * CRT Profile Catalogue & Runtime Picker
 * 
 * RESPONSIBILITIES:
 * - Define CRT visual profiles (frame size, buffer size, phosphor bounds)
 * - Select appropriate profile based on platform/viewport
 * - Handle viewport edge cases (0x0, narrow portrait, etc.)
 * 
 * ARCHITECTURE:
 * - Uses device.js for ALL platform detection (no duplication)
 * - Minimal caching for viewport-based selection
 * - Profile lock support for stability during authentication
 * 
 * @module engine/crtProfiles
 * @author UNBRANDED
 */

import { detectPlatform, PLATFORM, isMobileDevice } from '../device.js';

// ============================================================================
// CRT PROFILE DEFINITIONS
// ============================================================================

/**
 * CRT visual profiles
 * 
 * PROFILE CONTRACT (consumed by CRTShader.jsx):
 * - id: Unique identifier for cache/remount detection
 * - frame: { w, h } - Viewport/canvas dimensions
 * - buffer: { w, h } - DopeOS rendering buffer (affects text scale!)
 * - contentScale: Shader rendering scale (0.90 = 90% of phosphor area)
 * - phosphor: { left, right, top, bottom } - Phosphor bounds (normalized 0-1)
 * 
 * CRITICAL: These values are SACRED for visual consistency
 * - Changing buffer affects text/UI scale throughout the app
 * - Changing phosphor affects CRT bezel appearance
 * - Changing contentScale affects shader rendering area
 */
export const CRT_PROFILES = {
  /**
   * Desktop 4:3 Profile
   * - Traditional CRT monitor aspect ratio
   * - Visible bezel/frame around content
   * - 640x400 buffer = 1:1 text scale
   */
  DESKTOP_4_3: {
    id: '4_3',
    frame: { w: 960, h: 720 },
    buffer: { w: 640, h: 400 },
    contentScale: 1,
    phosphor: {
      left: 152 / 960,   // ~15.8% left margin
      right: 808 / 960,  // ~84.2% right edge
      top: 150 / 720,    // ~20.8% top margin
      bottom: 571 / 720, // ~79.3% bottom edge
    },
  },
  
  /**
   * Mobile 16:9 Profile
   * - Modern smartphone/tablet aspect ratio
   * - Minimal bezel (edge-to-edge)
   * - 360x640 buffer = 3x text scale (readable on mobile)
   */
  MOBILE_16_9: {
    id: '9_16',
    frame: { w: 1080, h: 1920 },
    buffer: { w: 360, h: 640 },  // 1/3 of frame for 3x text scale
    contentScale: 0.90,           // 90% rendering area - bezel visible
    phosphor: {
      left: 0.04,    // 4% left margin
      right: 0.96,   // 96% right edge
      top: 0.04,     // 4% top margin - bezel visible
      bottom: 0.96,  // 96% bottom edge - bezel visible
    },
  },
};

// ============================================================================
// PROFILE SELECTION CACHE
// ============================================================================

/**
 * Viewport-based cache to avoid recalculating on every frame
 * Invalidated when viewport dimensions change by >2px
 */
let profileCache = { w: 0, h: 0, profile: null };

/**
 * Force-clear profile cache for fresh evaluation
 * 
 * USE CASE: When SDKs inject asynchronously after initial render
 * 
 * @example
 * // After Farcaster SDK loads
 * resetCRTProfileCache();
 * const freshProfile = pickCRTProfile();
 */
export function resetCRTProfileCache() {
  profileCache = { w: 0, h: 0, profile: null };
}

// ============================================================================
// PROFILE PICKER (SINGLE SOURCE OF TRUTH)
// ============================================================================

/**
 * Select appropriate CRT profile for current environment
 * 
 * SELECTION LOGIC:
 * 1. Test mode → Always Desktop (for consistent screenshots)
 * 2. Profile lock active → Return cached profile (stability during auth)
 * 3. URL override (?profile=full or ?profile=4_3)
 * 4. Platform detection via device.js
 * 5. Viewport-based fallback (narrow portrait → mobile)
 * 
 * VIEWPORT EDGE CASES:
 * - 0x0 viewport (Base App after signing): Use last known or fallback to 960x720
 * - Narrow portrait (<1200px wide, portrait): Force mobile profile
 * 
 * PERFORMANCE:
 * - Cached when viewport unchanged (±2px tolerance)
 * - ~1ms typical execution time
 * 
 * @returns {Object} CRT profile object
 */
export function pickCRTProfile() {
  // Test mode: Always use Desktop for consistent rendering
  if (import.meta.env?.DCW_TEST_MODE === '1' || globalThis.DCW_TEST_MODE) {
    return CRT_PROFILES.DESKTOP_4_3;
  }

  // SSR guard
  if (typeof window === 'undefined') {
    return CRT_PROFILES.DESKTOP_4_3;
  }

  // Get current viewport dimensions
  let vw = window.innerWidth;
  let vh = window.innerHeight;

  // EDGE CASE: 0x0 viewport (Base App closes signing sheet)
  // Fallback chain: documentElement → sessionStorage → default 960x720
  if (vw < 100 || vh < 100) {
    const de = document.documentElement;
    vw = Math.max(vw, de?.clientWidth || 0);
    vh = Math.max(vh, de?.clientHeight || 0);

    // Try last known good viewport from sessionStorage
    try {
      const prev = JSON.parse(sessionStorage.getItem('dcw_last_viewport') || '{}');
      vw = Math.max(vw, prev.w || 0);
      vh = Math.max(vh, prev.h || 0);
    } catch (e) {
      // sessionStorage unavailable
    }

    // Ultimate fallback: Desktop dimensions
    if (vw < 100 || vh < 100) {
      vw = 960;
      vh = 720;
    }
  }

  // Persist good viewport for future reference
  try {
    sessionStorage.setItem('dcw_last_viewport', JSON.stringify({ w: vw, h: vh }));
  } catch (e) {
    // sessionStorage unavailable (private mode, etc.)
  }

  // Check viewport cache (avoid recalculation if unchanged)
  const vwChanged = Math.abs(vw - profileCache.w) > 2;
  const vhChanged = Math.abs(vh - profileCache.h) > 2;
  
  if (profileCache.profile && !vwChanged && !vhChanged) {
    // Profile lock active: Return cached profile unconditionally
    if (globalThis.__DCW_PROFILE_LOCK) {
      return profileCache.profile;
    }
    // No lock but viewport unchanged: Return cached
    return profileCache.profile;
  }

  // URL override: ?profile=full (bezel-less for screenshots)
  const urlParams = new URLSearchParams(window.location.search);
  const forcedProfile = urlParams.get('profile');
  
  if (forcedProfile === 'full') {
    const aspect = vw / vh;
    // CRTShader applies axisScale multiplier - pre-scale content inversely
    const axisScale = aspect < 0.5 ? 0.5 : aspect;
    
    return {
      id: 'full',
      frame: { w: vw, h: vh },
      buffer: { w: vw, h: vh },
      contentScale: 1 / axisScale,  // Cancel axisScale for edge-to-edge
      phosphor: { left: 0, right: 1, top: 0, bottom: 1 },
    };
  }

  // URL override: ?profile=4_3 or ?profile=MOBILE_16_9
  if (forcedProfile && CRT_PROFILES[forcedProfile.toUpperCase()]) {
    return CRT_PROFILES[forcedProfile.toUpperCase()];
  }

  // Platform detection via device.js (Single Source of Truth)
  const platform = detectPlatform();
  const isMobile = isMobileDevice();

  // Platform-based profile selection
  const useMobileProfile = (
    platform === PLATFORM.FARCASTER ||
    platform === PLATFORM.BASE_MINIKIT ||
    platform === PLATFORM.WORLDCOIN ||
    platform === PLATFORM.MOBILE ||
    isMobile
  );

  let selectedProfile = useMobileProfile 
    ? CRT_PROFILES.MOBILE_16_9 
    : CRT_PROFILES.DESKTOP_4_3;

  // Viewport-based override: Narrow portrait → Mobile
  // (Handles Farcaster desktop client, narrow browser windows, etc.)
  const isNarrowPortrait = (() => {
    try {
      // Only check top-level windows (not iframes)
      if (window.parent !== window) return false;
      
      const isPortrait = vh > vw * 1.05;  // 5% tolerance
      const isNarrow = vw <= 1200;
      
      return isNarrow && isPortrait;
    } catch (e) {
      return false;
    }
  })();

  if (isNarrowPortrait) {
    selectedProfile = CRT_PROFILES.MOBILE_16_9;
  }

  // Profile lock: Override selection to maintain stability
  // USE CASE: During authentication flow, prevent profile flicker
  if (globalThis.__DCW_PROFILE_LOCK && profileCache.profile) {
    // Exception: If mobile app detected but locked to desktop, break lock
    const lockedToDesktop = profileCache.profile.id === '4_3';
    if (useMobileProfile && lockedToDesktop) {
      console.warn('[CRT Profiles] Breaking profile lock: Mobile app detected with desktop profile cached');
      globalThis.__DCW_PROFILE_LOCK = false;
      profileCache = { w: 0, h: 0, profile: null };
      // Continue to selectedProfile below
    } else {
      return profileCache.profile;
    }
  }

  // Update cache and return
  profileCache = { w: vw, h: vh, profile: selectedProfile };
  
  // Store in global for debugging
  try {
    globalThis.__DCW_LAST_PROFILE = selectedProfile;
  } catch (e) {
    // Global unavailable
  }

  return selectedProfile;
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  CRT_PROFILES,
  pickCRTProfile,
  resetCRTProfileCache
};
